/**
 * The platform admin: the people who run the platform, over every restaurant.
 *
 * Used only by `apps/platform-web`, under its own `ApiProvider` and storage
 * key, because an operator's token is not a staff token: the server refuses it
 * on every restaurant and supplier route, and refuses theirs on
 * `/v1/platform/*`.
 *
 * **Signing in is always two steps.** A right password answers with a
 * challenge, never a session; the session comes from the code. An operator
 * with no authenticator app yet can use the challenge for one thing only:
 * setting one up (`useBeginOperatorSetup`, `useConfirmOperatorSetup`), which
 * ends by signing them in and handing over their recovery codes, once.
 *
 * The session has no refresh token. It lasts half an hour and is renewed
 * while the app is open (`useRenewOperatorSession`), never past a working day
 * from the code that began it; then the code is asked for again.
 *
 * Money is a decimal string in the currency the row carries (invoices and
 * plans are in the platform's billing currency). Pass it to `formatMoney`
 * explicitly; the browser never adds any of it up.
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError, SessionTokens } from './client';
import { useApi, useSession, useStartSession } from './provider';

// --------------------------------------------------------------------------- //
// Shapes
// --------------------------------------------------------------------------- //

export interface OperatorChallenge {
  mfa_required: true;
  challenge_token: string;
  /** Seconds, for the code or for setting up the app. */
  expires_in: number;
  /** True until this operator has an authenticator app. */
  setup_required: boolean;
}

export interface OperatorToken {
  access_token: string;
  token_type: string;
  /** Seconds. Half an hour; renewed while the app is open. */
  expires_in: number;
}

export interface OperatorEnrolment {
  /** Base32, for typing in when a camera will not read the QR code. */
  secret: string;
  /** `otpauth://totp/...` — render this as the QR code. */
  provisioning_uri: string;
}

export interface OperatorSetupDone extends OperatorToken {
  /** Shown once; stored hashed, and nobody can read them back. */
  recovery_codes: string[];
}

export interface Operator {
  id: string;
  email: string;
  name: string;
  signed_in_at: string;
  recovery_codes_left: number;
}

/** "trial": on a free trial of a higher plan; nothing is stopped. */
export type RestaurantStatus = 'active' | 'trial' | 'suspended';

export interface PlatformRestaurant {
  id: string;
  name: string;
  /** What the owner types at sign-in. */
  slug: string;
  created_at: string;
  plan_key: string | null;
  plan_name: string | null;
  status: RestaurantStatus;
  cycle: 'month' | 'year' | null;
  /** Sites open now. */
  sites: number;
  /** The last trading day with an order on it; null before the first. */
  last_order_on: string | null;
  /** When the free trial running now ends; null when none is. */
  trial_ends_at: string | null;
}

export interface NewRestaurantInput {
  restaurant_name: string;
  slug: string;
  owner_name: string;
  email: string;
  /** IANA zone of the first site. Required: a restaurant can be anywhere. */
  timezone: string;
  /** ISO 3166-1 alpha-2: where it trades. */
  country: string;
  /** ISO 4217: what it charges in. */
  currency: string;
  /** How numbers and dates are written; the country's own when left out. */
  locale?: string | null;
  location_name?: string | null;
  location_code?: string;
  /** Start on a free trial of this plan (above Starter). Both or neither. */
  trial_plan?: string | null;
  trial_days?: number | null;
}

export interface PlatformPerson {
  id: string;
  name: string;
  email: string;
  is_active: boolean;
}

export interface PlatformSite {
  id: string;
  name: string;
  code: string;
  timezone: string;
  currency: string;
  is_active: boolean;
}

export interface PlatformPlan {
  key: string;
  name: string;
  /** Higher is more. */
  tier: number;
  price_month: string | null;
  price_year: string | null;
}

/** A free trial the platform gave. During it, the subscription's plan is the trial's. */
export interface PlatformTrial {
  plan: PlatformPlan;
  /** What the restaurant goes back to when it ends. */
  previous_plan: PlatformPlan;
  previous_cycle: 'month' | 'year';
  starts_at: string;
  ends_at: string;
  /** Whole days to go, a part day counted as one. */
  days_left: number;
  note: string;
  started_by_email: string;
}

export interface PlatformInvoice {
  id: string;
  number: string;
  description: string;
  amount: string;
  currency: string;
  status: 'open' | 'paid' | 'void';
  issued_at: string;
  due_on: string;
  paid_at: string | null;
  payment_reference: string;
}

export interface PlatformActivity {
  id: string;
  occurred_at: string;
  operator_email: string;
  /** A fixed word: "signed_in", "invoice.paid", "restaurant.suspended". */
  action: string;
  restaurant_id: string | null;
  restaurant_name: string | null;
  summary: string;
  details: Record<string, unknown>;
  address: string | null;
}

export interface PlatformRestaurantDetail {
  id: string;
  name: string;
  slug: string;
  country: string;
  /** The restaurant's own currency and locale, for anything of theirs. */
  currency: string;
  locale: string;
  status: RestaurantStatus;
  owners: PlatformPerson[];
  sites: PlatformSite[];
  subscription: {
    plan: PlatformPlan;
    status: RestaurantStatus;
    cycle: 'month' | 'year';
    current_period_end: string | null;
    /** The platform's billing currency: plan prices and invoices. */
    currency: string;
    pending: {
      id: string;
      plan: PlatformPlan;
      cycle: 'month' | 'year';
      note: string;
      requested_at: string;
    } | null;
    trial: PlatformTrial | null;
  };
  invoices: PlatformInvoice[];
  /** The latest platform activity about this restaurant. */
  activity: PlatformActivity[];
}

export interface PlanRequest {
  id: string;
  restaurant_id: string;
  restaurant_name: string;
  restaurant_slug: string;
  plan_key: string;
  plan_name: string;
  cycle: 'month' | 'year';
  note: string;
  requested_at: string;
  /** Null: agreed case by case, so approving with an invoice needs an amount. */
  list_price: string | null;
  currency: string;
}

export type InvoiceFilter = 'all' | 'open' | 'overdue' | 'paid' | 'void';

export interface PlatformInvoiceRow extends PlatformInvoice {
  restaurant_id: string;
  restaurant_name: string;
  restaurant_slug: string;
  /** Open and past its due date. */
  overdue: boolean;
}

export interface Mailed {
  sent_to: string[];
  /** Owners the email could not reach; the change stands regardless. */
  failed: string[];
}

export interface PlanChoice {
  key: string;
  name: string;
  tier: number;
  price_month: string | null;
  price_year: string | null;
  /** Built features, as keys; `feature_names` says them in words. */
  features: string[];
}

export interface PlanCatalog {
  plans: PlatformPlan[];
  /** Every restaurant opens on this; a trial on opening is of a plan above it. */
  opening_plan: string;
  trial_max_days: number;
}

/** Every plan, for moving a restaurant or giving it a trial. */
export interface PlanChoices {
  /** The plan it is on now (during a trial, the trial's). */
  current: string;
  /** The plan paid for: during a trial, the one it goes back to. */
  paying: string;
  options: PlanChoice[];
  feature_names: Record<string, string>;
  /** The longest free trial, in days from today. */
  trial_max_days: number;
  currency: string;
}

export interface ActivityPage {
  entries: PlatformActivity[];
  next_before: string | null;
}

/** An operator's session as the client stores it: no refresh token behind it. */
export function operatorSession(token: OperatorToken, now = Date.now()): SessionTokens {
  return {
    access: token.access_token,
    refresh: null,
    expiresAt: now + token.expires_in * 1000,
  };
}

// --------------------------------------------------------------------------- //
// Signing in
// --------------------------------------------------------------------------- //

/**
 * Every call here that answers 401 is an answer about what was typed, not
 * about a session — there is none yet. So none of them may end one.
 */
const QUIET = { unauthenticatedEndsSession: false } as const;

export function useOperatorSignIn() {
  const api = useApi();
  return useMutation<OperatorChallenge, ApiError, { email: string; password: string }>({
    mutationFn: (input) =>
      api.post<OperatorChallenge>('/v1/platform/auth/sign-in', input, QUIET),
  });
}

export function useOperatorCode() {
  const api = useApi();
  const start = useStartSession();
  return useMutation<OperatorToken, ApiError, { challengeToken: string; code: string }>({
    mutationFn: ({ challengeToken, code }) =>
      api.post<OperatorToken>(
        '/v1/platform/auth/code',
        { challenge_token: challengeToken, code },
        QUIET,
      ),
    onSuccess: (token) => start(operatorSession(token)),
  });
}

export function useBeginOperatorSetup() {
  const api = useApi();
  return useMutation<OperatorEnrolment, ApiError, { challengeToken: string }>({
    mutationFn: ({ challengeToken }) =>
      api.post<OperatorEnrolment>(
        '/v1/platform/auth/setup',
        { challenge_token: challengeToken },
        QUIET,
      ),
  });
}

/**
 * Prove the phone has the secret. Answers with the recovery codes and a
 * session, but does not start the session: the screen shows the codes first,
 * and starts it (`useStartSession` with `operatorSession`) once they are saved.
 */
export function useConfirmOperatorSetup() {
  const api = useApi();
  return useMutation<OperatorSetupDone, ApiError, { challengeToken: string; code: string }>({
    mutationFn: ({ challengeToken, code }) =>
      api.post<OperatorSetupDone>(
        '/v1/platform/auth/setup/confirm',
        { challenge_token: challengeToken, code },
        QUIET,
      ),
  });
}

/** Always 204, whether or not the address is an operator's. */
export function useOperatorForgotPassword() {
  const api = useApi();
  return useMutation<void, ApiError, { email: string }>({
    mutationFn: (input) => api.post<void>('/v1/platform/auth/password/forgot', input, QUIET),
  });
}

export function useOperatorResetPassword() {
  const api = useApi();
  return useMutation<void, ApiError, { token: string; new_password: string }>({
    mutationFn: (input) => api.post<void>('/v1/platform/auth/password/reset', input, QUIET),
  });
}

/**
 * A fresh half hour on the same sign-in. Refused (401) once the sign-in is a
 * working day old, and the client's ordinary "session over" path then asks
 * for the password and code again.
 */
export function useRenewOperatorSession() {
  const api = useApi();
  return useMutation<OperatorToken, ApiError, void>({
    mutationFn: () => api.post<OperatorToken>('/v1/platform/auth/renew'),
    onSuccess: (token) => api.tokens.set(operatorSession(token)),
  });
}

/** Recorded on the platform's log, then the session ends here whatever the answer. */
export function useOperatorSignOut() {
  const api = useApi();
  const session = useSession();
  return useMutation<void, ApiError, void>({
    mutationFn: () => api.post<void>('/v1/platform/auth/sign-out', undefined, QUIET),
    onSettled: () => session.signOut(),
  });
}

export function useOperator() {
  const api = useApi();
  const session = useSession();
  return useQuery({
    queryKey: ['platform', 'me', session.epoch],
    queryFn: ({ signal }) => api.get<Operator>('/v1/platform/me', signal),
    enabled: session.isAuthenticated,
  });
}

/** For a code from the app (or a last recovery code). The old set stops working. */
export function useFreshOperatorRecoveryCodes() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<{ recovery_codes: string[] }, ApiError, { code: string }>({
    mutationFn: (input) =>
      api.post<{ recovery_codes: string[] }>('/v1/platform/me/recovery-codes', input, QUIET),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['platform', 'me'] }),
  });
}

// --------------------------------------------------------------------------- //
// Restaurants
// --------------------------------------------------------------------------- //

function refreshPlatform(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: ['platform'] });
}

export function usePlatformRestaurants(search: string) {
  const api = useApi();
  const q = search.trim();
  return useQuery({
    queryKey: ['platform', 'restaurants', q],
    queryFn: ({ signal }) =>
      api.get<PlatformRestaurant[]>(
        `/v1/platform/restaurants${q ? `?q=${encodeURIComponent(q)}` : ''}`,
        signal,
      ),
    placeholderData: (previous) => previous,
  });
}

export function usePlatformRestaurant(id: string) {
  const api = useApi();
  return useQuery({
    queryKey: ['platform', 'restaurant', id],
    queryFn: ({ signal }) =>
      api.get<PlatformRestaurantDetail>(`/v1/platform/restaurants/${id}`, signal),
  });
}

/** The owner is emailed a link to set their password. 409/422 name the field. */
export interface PlatformRegionSuggestion {
  country: string;
  currency: string | null;
  locale: string | null;
}

/** The currency and number format a restaurant in this country most likely wants. */
export function usePlatformRegionSuggestion(country: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['platform', 'region-suggestion', country],
    queryFn: ({ signal }) =>
      api.get<PlatformRegionSuggestion>(
        `/v1/platform/region-suggestion?country=${country}`,
        signal,
      ),
    enabled: Boolean(country),
    staleTime: Infinity,
  });
}

export function useOpenRestaurant() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<{ id: string }, ApiError, NewRestaurantInput>({
    mutationFn: (input) => api.post<{ id: string }>('/v1/platform/restaurants', input),
    onSuccess: () => refreshPlatform(queryClient),
  });
}

/** Suspend or lift a suspension. A reason is required; the owner reads it. */
export function useSetRestaurantSuspended() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { id: string; suspended: boolean; reason: string }>({
    mutationFn: ({ id, suspended, reason }) =>
      api.post<void>(`/v1/platform/restaurants/${id}/${suspended ? 'suspend' : 'resume'}`, {
        reason,
      }),
    onSettled: () => refreshPlatform(queryClient),
  });
}

// --------------------------------------------------------------------------- //
// A restaurant's plan: moving it directly, and free trials
// --------------------------------------------------------------------------- //

/** The plans on sale, for opening a restaurant on a free trial. */
export function usePlatformPlanCatalog() {
  const api = useApi();
  return useQuery({
    queryKey: ['platform', 'plans'],
    queryFn: ({ signal }) => api.get<PlanCatalog>('/v1/platform/plans', signal),
    staleTime: 5 * 60_000,
  });
}

export function usePlatformPlanChoices(restaurantId: string) {
  const api = useApi();
  return useQuery({
    queryKey: ['platform', 'restaurant', restaurantId, 'plans'],
    queryFn: ({ signal }) =>
      api.get<PlanChoices>(`/v1/platform/restaurants/${restaurantId}/plans`, signal),
  });
}

/**
 * Move a restaurant to a plan from today, up or down, without a request.
 * Ends a running free trial as converted. The owners are emailed.
 */
export function useChangeRestaurantPlan() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    {
      invoice: PlatformInvoice | null;
      mailed: Mailed;
      /** A request the restaurant was waiting on, approved by this. */
      answered_request: boolean;
      ended_trial: boolean;
    },
    ApiError,
    {
      restaurantId: string;
      plan: string;
      cycle: 'month' | 'year';
      invoice: boolean;
      /** In place of the list price; null keeps it. Required where there is none. */
      amount: string | null;
      note: string;
      reason: string;
    }
  >({
    mutationFn: ({ restaurantId, ...body }) =>
      api.post(`/v1/platform/restaurants/${restaurantId}/plan`, body),
    onSettled: () => refreshPlatform(queryClient),
  });
}

export function useStartTrial() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    { trial: PlatformTrial; mailed: Mailed },
    ApiError,
    { restaurantId: string; plan: string; days: number; note: string }
  >({
    mutationFn: ({ restaurantId, ...body }) =>
      api.post(`/v1/platform/restaurants/${restaurantId}/trial`, body),
    onSettled: () => refreshPlatform(queryClient),
  });
}

/** A later last day (`YYYY-MM-DD`), within the same cap from today. */
export function useExtendTrial() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    PlatformTrial,
    ApiError,
    { restaurantId: string; ends_on: string; reason: string }
  >({
    mutationFn: ({ restaurantId, ...body }) =>
      api.post(`/v1/platform/restaurants/${restaurantId}/trial/extend`, body),
    onSettled: () => refreshPlatform(queryClient),
  });
}

/** Back to the plan it was on before, at once. The owners are told. */
export function useEndTrial() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    { plan: PlatformPlan; cycle: 'month' | 'year'; lost: string[]; mailed: Mailed },
    ApiError,
    { restaurantId: string; reason: string }
  >({
    mutationFn: ({ restaurantId, reason }) =>
      api.post(`/v1/platform/restaurants/${restaurantId}/trial/end`, { reason }),
    onSettled: () => refreshPlatform(queryClient),
  });
}

// --------------------------------------------------------------------------- //
// Billing
// --------------------------------------------------------------------------- //

export function usePlanRequests() {
  const api = useApi();
  return useQuery({
    queryKey: ['platform', 'plan-requests'],
    queryFn: ({ signal }) => api.get<PlanRequest[]>('/v1/platform/plan-requests', signal),
  });
}

export function useApprovePlanRequest() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    { invoice: PlatformInvoice | null; mailed: Mailed | null },
    ApiError,
    {
      restaurantId: string;
      requestId: string;
      /** The agreed price in place of the list price; null keeps the list price. */
      amount: string | null;
      invoice: boolean;
      note: string;
    }
  >({
    mutationFn: ({ restaurantId, requestId, amount, invoice, note }) =>
      api.post(`/v1/platform/restaurants/${restaurantId}/plan-requests/${requestId}/approve`, {
        amount,
        invoice,
        note,
      }),
    onSettled: () => refreshPlatform(queryClient),
  });
}

export function useDeclinePlanRequest() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { restaurantId: string; requestId: string; note: string }>(
    {
      mutationFn: ({ restaurantId, requestId, note }) =>
        api.post<void>(
          `/v1/platform/restaurants/${restaurantId}/plan-requests/${requestId}/decline`,
          { note },
        ),
      onSettled: () => refreshPlatform(queryClient),
    },
  );
}

export function usePlatformInvoices(show: InvoiceFilter, restaurantId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['platform', 'invoices', show, restaurantId],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({ show });
      if (restaurantId) params.set('restaurant_id', restaurantId);
      return api.get<PlatformInvoiceRow[]>(
        `/v1/platform/invoices?${params.toString()}`,
        signal,
      );
    },
    placeholderData: (previous) => previous,
  });
}

export function useIssuePlatformInvoice() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    { invoice: PlatformInvoice; mailed: Mailed },
    ApiError,
    { restaurantId: string; amount: string; description: string }
  >({
    mutationFn: ({ restaurantId, amount, description }) =>
      api.post(`/v1/platform/restaurants/${restaurantId}/invoices`, { amount, description }),
    onSettled: () => refreshPlatform(queryClient),
  });
}

export function useMarkInvoicePaid() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    PlatformInvoice,
    ApiError,
    { restaurantId: string; number: string; reference: string }
  >({
    mutationFn: ({ restaurantId, number, reference }) =>
      api.post(
        `/v1/platform/restaurants/${restaurantId}/invoices/${encodeURIComponent(number)}/paid`,
        { reference },
      ),
    onSettled: () => refreshPlatform(queryClient),
  });
}

export function useVoidInvoice() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    PlatformInvoice,
    ApiError,
    { restaurantId: string; number: string; reason: string }
  >({
    mutationFn: ({ restaurantId, number, reason }) =>
      api.post(
        `/v1/platform/restaurants/${restaurantId}/invoices/${encodeURIComponent(number)}/void`,
        { reason },
      ),
    onSettled: () => refreshPlatform(queryClient),
  });
}

// --------------------------------------------------------------------------- //
// Operator activity
// --------------------------------------------------------------------------- //

export function usePlatformActivity(restaurantId: string | null = null) {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: ['platform', 'activity', restaurantId],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) => {
      const params = new URLSearchParams();
      if (restaurantId) params.set('restaurant_id', restaurantId);
      if (pageParam) params.set('before', pageParam);
      const query = params.toString();
      return api.get<ActivityPage>(`/v1/platform/activity${query ? `?${query}` : ''}`, signal);
    },
    getNextPageParam: (last) => last.next_before,
  });
}

// --------------------------------------------------------------------------- //
// Enquiries from the public site's contact form
// --------------------------------------------------------------------------- //

export interface Enquiry {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  restaurant: string;
  message: string | null;
  /** The plan they asked about, when they came from a plan's button. */
  plan: 'starter' | 'professional' | 'enterprise' | null;
  /** The page of the public site they wrote from. */
  page: string | null;
  created_at: string;
  /** Null until an operator marks it answered. */
  handled_at: string | null;
}

export function usePlatformEnquiries() {
  const api = useApi();
  return useQuery({
    queryKey: ['platform', 'enquiries'],
    queryFn: ({ signal }) => api.get<Enquiry[]>('/v1/platform/enquiries', signal),
    // Someone may be waiting for an answer: looked at again every minute.
    refetchInterval: 60_000,
  });
}

/** Mark an enquiry answered, or put it back as not answered yet. */
export function useMarkEnquiryAnswered() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { id: string; answered: boolean }>({
    mutationFn: ({ id, answered }) =>
      api.request<void>(`/v1/platform/enquiries/${id}/answered`, {
        method: 'PUT',
        body: { answered },
      }),
    onSettled: () =>
      void queryClient.invalidateQueries({ queryKey: ['platform', 'enquiries'] }),
  });
}
